import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  apiErrorCode,
  resolveShortLink,
  reissueFormLink,
  verifyFormPackageV2,
  getFormPackageV2View,
  submitFormPackageV2,
  type ShortLinkResponse,
  type ShortLinkFormPackageV2,
  type FormPackageV2SubmitPayload,
} from '../api/bookingApi';
import Shell from '../components/Shell';
import LinkReissueStep from '../components/LinkReissueStep';
import { HouseholdHub } from '../components/HouseholdHub';
import { V2VerifyStep } from '../components/form-package-v2/V2VerifyStep';
import FormPackageV2Renderer from '../components/form-package-v2/FormPackageV2Renderer';
import { useKioskV2Session } from '../components/form-package-v2/KioskV2SessionContext';
import { adaptAndValidateV2Package } from '../components/form-package-v2/validateV2Package';
import { clearV2Session } from '../components/form-package-v2/v2Session';
import {
  readVerifiedToken,
  writeVerifiedToken,
  clearVerifiedToken,
} from '../components/form-package-v2/verifiedSession';
import {
  clearKioskReturn,
  readKioskReturn,
} from '../components/form-package-v2/kioskReturn';
import type { V2Package } from '../components/form-package-v2/types';
import { emptyInsuranceLedger, type InsuranceLedger } from '../components/form-package-v2/insuranceCheck';
import { usePracticeInfo } from '../hooks/usePracticeInfo';
import LoadingState from '../components/LoadingState';
import StatusScreen from '../components/StatusScreen';
import SubmitErrorBanner from '../components/SubmitErrorBanner';

type PageState =
  | 'loading' | 'form' | 'submitting' | 'success' | 'expired' | 'not_found' | 'error'
  | 'verify'              // #form-verify: locked v2 package awaiting DOB confirmation
  | 'household'           // #2277: a household link resolved to several members; pick one
  | 'already_submitted'   // v2: the bearer already submitted this package (resolve 410, submit 409)
  | 'call_office'         // fail closed: unknown link type, unrenderable v2 package, staff-action refusal
  | 'reissue' | 'reissue_sent' | 'reissue_try_later' | 'reissue_uncertain'
  | 'link_retired';       // a retired V1 link type (custom_form, form_package, prefill): the lane it opened is gone (backend#1604)

const CALL_OFFICE_DEFAULT = "Please give the office a call and we'll get your forms sorted.";

interface ApiError extends Error {
  status?: number;
}

export default function ShortLinkPage() {
  const { shortCode } = useParams<{ shortCode: string }>();
  const navigate = useNavigate();
  const retainedKiosk = useKioskV2Session();

  const [state, setState] = useState<PageState>('loading');
  const [data, setData] = useState<ShortLinkResponse | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [reissueError, setReissueError] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [v2Pkg, setV2Pkg] = useState<V2Package | null>(null);
  const [callOfficeMessage, setCallOfficeMessage] = useState<string | null>(null);
  const [kioskReturnPath, setKioskReturnPath] = useState<string | null>(null);
  // Bumped to remount the v2 renderer (fresh state + uploads) after an upload-state refusal.
  const [rendererKey, setRendererKey] = useState(0);
  // The live insurance check's bearer (booking-frontend#46): only from this tab's
  // DOB verify, held in this state and nowhere else (never storage, the draft or
  // the URL). A refresh resumes with the cached link token and no proof, so no
  // check runs after one.
  const [dobProofToken, setDobProofToken] = useState<string | null>(null);
  // Insurance-check attempts per package instance, for this page's life. Held here
  // rather than in the renderer so a remount (rendererKey) cannot reset the count
  // and send a third check. Memory only, like the proof.
  const insuranceLedgers = useRef<Record<string, InsuranceLedger>>({});
  const insuranceLedgerFor = (packageInstanceId: string) =>
    (insuranceLedgers.current[packageInstanceId] ??= emptyInsuranceLedger());
  // The v2 `business` block carries no phone, and a retired V1 link carries no `business`
  // block at all; keep the lazy fetch enabled throughout expired-link recovery.
  const { info: practice } = usePracticeInfo(data?.client_id,
    state === 'call_office' || state === 'link_retired' || state === 'expired' || state.startsWith('reissue'));

  // Render an unlocked v2 package (from verify or the cached-token resume). Fail
  // closed: anything this build cannot render exactly is a dead end. On success the
  // token is cached so a refresh within the tab skips re-verification.
  const unlockPackage = (unlocked: ShortLinkFormPackageV2) => {
    // The proof is split off so `data` never carries it.
    const { dob_proof_token: proof, ...view } = unlocked;
    setDobProofToken(typeof proof === 'string' && proof !== '' ? proof : null);
    const validated = adaptAndValidateV2Package(view);
    setData(view);
    if (validated.ok) {
      setV2Pkg(validated.pkg);
      setKioskReturnPath(readKioskReturn(validated.pkg.packageInstanceId)?.return_path ?? null);
      writeVerifiedToken(view.package_instance_id, view.token);
      setState('form');
    } else {
      console.warn('form_package_v2 link failed closed:', validated.reason, validated.detail);
      setState('call_office');
    }
  };

  const handleVerifySubmit = async (dob: string) => {
    if (!data || data.link_type !== 'form_package_v2' || !shortCode) return;
    const clientId = data.client_id;
    setVerifying(true);
    setVerifyError(null);
    try {
      const view = await verifyFormPackageV2(clientId, shortCode, dob);
      setVerifying(false);
      unlockPackage(view);
    } catch (err: unknown) {
      setVerifying(false);
      const status = (err as ApiError).status;
      const code = apiErrorCode(err);
      if (status === 429 || code === 'too_many_attempts') {
        setVerifyError('Too many attempts. Please call the office to complete your forms.');
      } else if (status === 401) {
        setVerifyError("That date of birth doesn't match our records. Please try again.");
      } else if (status === 404) {
        setState('expired');
      } else if (status === 410 && code === 'form_package_consumed') {
        setState('already_submitted');
      } else {
        setVerifyError("We couldn't verify right now. Please try again in a moment.");
      }
    }
  };

  useEffect(() => {
    if (!shortCode) {
      setState('error');
      return;
    }

    resolveShortLink(shortCode)
      .then((res) => {
        switch (res.link_type) {
          case 'custom_form':
          case 'form_package':
          case 'prefill':
            // The V1 lanes these opened are retired (operaitor/backend#1604: the prefill
            // forms-only booking flow in slice 1, the custom-form and V1 package renderers in
            // slice 2). Forms now arrive only as v2 package links, so a pre-cutover link an
            // older backend still resolves is a dead end shown exactly as an expired link.
            // The new resolver answers 404 form_link_retired, also offering recovery.
            setData(res);
            setState('link_retired');
            return;
          case 'form_package_v2': {
            // Locked (backend#form-verify): resolve now withholds the token + forms
            // until DOB is confirmed. Try a token cached from an earlier verify in
            // this tab (so a refresh resumes silently); otherwise prompt.
            if ('verification_required' in res && res.verification_required) {
              setData(res);
              const cached = readVerifiedToken(res.package_instance_id);
              if (cached) {
                getFormPackageV2View(res.client_id, cached)
                  .then((view) => unlockPackage(view))
                  .catch(() => { clearVerifiedToken(res.package_instance_id); setState('verify'); });
              } else {
                setState('verify');
              }
              return;
            }
            // Already-unlocked payload (older backend that returns the token inline).
            unlockPackage(res as ShortLinkFormPackageV2);
            return;
          }
          case 'form_package_household': {
            // Pure hub: each member carries their own /f/{short_code}, so no package
            // is loaded here — picking one navigates into the existing v2 lane.
            setData(res);
            setState(res.members.length > 0 ? 'household' : 'not_found');
            return;
          }
          default:
            console.warn('Unknown short-link type:', (res as { link_type?: unknown }).link_type);
            setData(res);
            setState('call_office');
        }
      })
      .catch((err: ApiError) => {
        if (err.status === 401) setState('expired');
        else if (err.status === 404) {
          const deadLink = ['form_package_unavailable', 'form_link_retired'].includes(apiErrorCode(err) ?? '');
          setState(deadLink ? 'expired' : 'not_found');
        }
        else if (err.status === 410) {
          setKioskReturnPath(readKioskReturn()?.return_path ?? null);
          setState('already_submitted');
        }
        else setState('error');
      });
  }, [shortCode]);

  // A retired V1 link carries no `business` block — fall back to the fetched practice info.
  const practiceInfo = data && 'business' in data && data.business ? {
    name: data.business.name,
    logo_url: data.business.logo_url,
  } : practice ? {
    name: practice.name,
    logo_url: practice.logo_url,
  } : null;

  const handlePackageV2Submit = async (payload: FormPackageV2SubmitPayload) => {
    if (!data || data.link_type !== 'form_package_v2' || !('token' in data) || !v2Pkg) return;
    setState('submitting');
    setSubmitError(null);
    try {
      await submitFormPackageV2(data.client_id, data.token, payload);
      clearV2Session(v2Pkg.packageInstanceId); clearVerifiedToken(v2Pkg.packageInstanceId);
      setDobProofToken(null);
      setState('success');
    } catch (err: unknown) {
      const status = (err as ApiError).status;
      const code = apiErrorCode(err);
      if (status === 401) { setState('expired'); return; }
      if (status === 404) { setState('not_found'); return; }
      if (status === 410) {
        // `form_package_unavailable` = expired/revoked; only the resolver says "consumed".
        if (code === 'form_package_consumed') { clearV2Session(v2Pkg.packageInstanceId); clearVerifiedToken(v2Pkg.packageInstanceId); setState('already_submitted'); }
        else setState('expired');
        return;
      }
      if (status === 409 && (code === 'idempotency_conflict' || code === 'package_already_consumed')) {
        // Consumed by this bearer's earlier attempt (a lost response, a second tab).
        clearV2Session(v2Pkg.packageInstanceId); clearVerifiedToken(v2Pkg.packageInstanceId);
        setState('already_submitted');
        return;
      }
      if ((status === 409 && (code === 'form_package_incompatible' || code === 'form_submission_identity_required')) || status === 400) {
        // Staff action needed, or this client built a document the server rejects: retrying cannot help.
        console.warn('form_package_v2 submit refused:', status, code);
        setCallOfficeMessage("We can't finish these forms online. Please call the office and we'll take care of it.");
        setState('call_office');
        return;
      }
      if (status === 409) {
        // An upload is no longer valid server-side; the only safe recovery is to sign again.
        setRendererKey((k) => k + 1);
        setSubmitError("We couldn't verify your signatures. Please sign again.");
        setState('form');
        return;
      }
      if (status === 429) {
        setSubmitError('Please wait a moment and try again.');
        setState('form');
        return;
      }
      // 503 (acceptance dark or transient), other 5xx, network: nothing was accepted, keep everything.
      setSubmitError("We couldn't submit your forms right now. Please try again in a moment.");
      setState('form');
    }
  };

  const openReissue = () => { setReissueError(null); setState('reissue'); };

  const handleReissue = async (phone: string, dob: string) => {
    if (!shortCode) return;
    setReissueError(null);
    try {
      const result = await reissueFormLink(shortCode, phone, dob);
      if (result.outcome === 'try_later') setRetryAfter(result.retry_after);
      setState(`reissue_${result.outcome}`);
    } catch (err) {
      const status = (err as ApiError).status;
      const code = apiErrorCode(err);
      if (status === 401 && code === 'verification_failed') {
        setReissueError("Those details don't match our records. Please try again.");
      } else if (status === 409 && code === 'form_link_still_live') {
        navigate(0);
      } else if (status === 410) {
        setState('already_submitted');
      } else if (status === 404) {
        setState('not_found');
      } else if (status === 409 || (status === 429 && (code === 'too_many_attempts' || code === 'reissue_limited'))) {
        setCallOfficeMessage(
          status === 429 && code === 'too_many_attempts'
            ? 'Too many verification attempts. Please call the office to get a new link.'
            : status === 429 && code === 'reissue_limited'
              ? "You've reached the limit for new-link requests in 24 hours. Please call the office."
              : code === 'sms_opted_out'
                ? "You've opted out of texts. Please call the office to get your forms."
                : "We can't send a new link online. Please call the office to get your forms."
        );
        setState('call_office');
      } else {
        setReissueError("We couldn't send a new link right now. Please try again in a moment.");
      }
    }
  };

  function returnToKiosk() {
    if (!kioskReturnPath) return;
    const retained = retainedKiosk.retained;
    if (state === 'success' && v2Pkg && data?.link_type === 'form_package_v2'
      && retained?.clientId === data.client_id
      && kioskReturnPath === `/kiosk/${data.client_id}`) {
      retainedKiosk.markSubmitted(data.client_id, retained.session.kiosk_token);
    }
    const returnPath = kioskReturnPath;
    clearKioskReturn();
    navigate(returnPath);
  }

  if (state === 'loading') {
    return (
      <Shell practiceInfo={practiceInfo}>
        <LoadingState message="Loading..." />
      </Shell>
    );
  }

  // The v2 renderer runs full-bleed on phones (sticky chrome, one page scroll);
  // terminal states and every other link type keep the classic card.
  const v2Active = data?.link_type === 'form_package_v2' && (state === 'form' || state === 'submitting');

  return (
    <Shell practiceInfo={practiceInfo} fullBleed={v2Active}>
      <AnimatePresence mode="wait">
        {(state === 'expired' || state === 'link_retired') && (
          <StatusScreen type="expired" heading="This link has expired"
            message="Request a new text to continue your forms."
            actionLabel="Text me a new link" onAction={openReissue} />
        )}
        {state === 'reissue' && (
          <LinkReissueStep onSubmit={handleReissue} error={reissueError} onEdit={() => setReissueError(null)} />
        )}
        {state === 'reissue_sent' && (
          <StatusScreen type="success" heading="We've texted you a new link"
            message="Open the newest text from the practice to continue your forms." />
        )}
        {state === 'reissue_try_later' && retryAfter && (
          <StatusScreen type="expired" heading="Please try again later"
            message={`The text couldn't be sent right now. Try again after ${new Intl.DateTimeFormat(undefined, {
              year: 'numeric', month: 'long', day: 'numeric',
              hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
            }).format(new Date(retryAfter))}.`}
            actionLabel="Try again" onAction={openReissue} />
        )}
        {state === 'reissue_uncertain' && (
          <StatusScreen type="expired" heading="Check your texts"
            message="Your new link may be on its way: check your texts in a few minutes. If nothing arrives, try again or call the office"
            actionLabel="Try again" onAction={openReissue} />
        )}
        {state === 'not_found' && <StatusScreen type="not_found" />}
        {state === 'error' && !data && <StatusScreen type="error" />}
        {state === 'success' && (
          <StatusScreen
            type="success"
            heading="All Forms Submitted!"
            actionLabel={kioskReturnPath ? 'Back to kiosk' : undefined}
            onAction={kioskReturnPath ? returnToKiosk : undefined}
          />
        )}
        {state === 'already_submitted' && (
          <StatusScreen
            type="success"
            heading="Already submitted"
            message="These forms were already received. There's nothing more to do."
            actionLabel={kioskReturnPath ? 'Back to kiosk' : undefined}
            onAction={kioskReturnPath ? returnToKiosk : undefined}
          />
        )}
        {state === 'household' && data?.link_type === 'form_package_household' && (
          <HouseholdHub
            members={data.members}
            onSelect={(member) => navigate(`/f/${member.short_code}`)}
          />
        )}
        {state === 'verify' && data?.link_type === 'form_package_v2' && (
          <V2VerifyStep
            practiceName={practiceInfo?.name ?? 'Our office'}
            formsCount={'forms_count' in data ? data.forms_count : 0}
            verifying={verifying}
            error={verifyError}
            onVerify={handleVerifySubmit}
            onEdit={() => setVerifyError(null)}
          />
        )}
        {state === 'call_office' && (
          <StatusScreen
            type="call_office"
            heading="We can't complete these forms online"
            message={callOfficeMessage ?? CALL_OFFICE_DEFAULT}
            phoneNumber={practice?.phone_number ?? undefined}
          />
        )}

        {(state === 'form' || state === 'submitting') && data && (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className={v2Active ? 'flex-1 flex flex-col' : undefined}
          >
            {/* In the full-bleed v2 layout the banner needs its own gutter (the
                content padding lives inside the renderer). */}
            {v2Active && submitError ? (
              <div className="px-5 pt-4 sm:px-0 sm:pt-0">
                <SubmitErrorBanner message={submitError} />
              </div>
            ) : (
              <SubmitErrorBanner message={submitError} />
            )}
            {data.link_type === 'form_package_v2' && 'token' in data && v2Pkg && (
              <FormPackageV2Renderer
                key={rendererKey}
                pkg={v2Pkg}
                clientId={data.client_id}
                token={data.token}
                onSubmit={handlePackageV2Submit}
                loading={state === 'submitting'}
                practiceInfo={practiceInfo}
                dobProofToken={dobProofToken}
                insuranceLedger={insuranceLedgerFor(v2Pkg.packageInstanceId)}
                onLinkInvalid={(err) => {
                  if (err.kind === 'expired' || err.kind === 'unavailable') setState('expired');
                }}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </Shell>
  );
}
